import { Id, isSuccess, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { contactFilterService } from "../shared/api/contactFilterService";
import { useContactAttributeOptions } from "../shared/hooks/useContactAttributeOptions";
import { ContactFilterFormValues, ContactFilterUpdateResource, IContactFilterItemResource } from "../shared/types";
import { ContactFilterBarItem } from "./ContactFilterBarItem";
import { useContactFilterQuery } from "./ContactFilterQuery";
import { mapFormValuesToRequest } from "./mappers";

interface Props {
	contactFilterId: Id | null;
	onApply: (filterId: Id | null) => void;
	onClick: () => void;
}

const FilterList = styled.div`
	padding: ${({ theme }) => theme.spacing(2)} 0;
	display: flex;
	flex-wrap: wrap;
	gap: ${({ theme }) => theme.spacing(2)};
`;

export const ContactFilterBar: FC<Props> = ({ contactFilterId, onApply, onClick }) => {
	const { t } = useTranslation();
	const { showToast } = useToasts();
	const { data: contactFilter } = useContactFilterQuery({
		contactFilterId,
		onNotFound: () =>
			showToast({
				type: "warning",
				title: t("Contact filter"),
				body: t("The contact filter no longer exists and cannot be applied."),
			}),
	});
	const { contactAttributeOptions } = useContactAttributeOptions();
	const queryClient = useQueryClient();

	const handleRemoveFilterItem = useCallback(
		async (filter: ContactFilterUpdateResource, filterItem: IContactFilterItemResource) => {
			const nextItems = filter.items.filter((other) => other !== filterItem);
			if (nextItems.length === 0) {
				onApply(null);
				return;
			}

			const formValues: ContactFilterFormValues = {
				translations: filter.translations,
				type: "onDemand",
				showInSidebar: false,
				filterItems: nextItems.map((item) => ({
					contactAttributeId: item.contactAttribute.id,
					operator: item.operator,
					value: item.value,
					relationColumn: item.relationColumn,
				})),
			};

			const response = await contactFilterService.store(mapFormValuesToRequest(formValues, contactAttributeOptions));
			if (isSuccess(response)) {
				await queryClient.invalidateQueries({ queryKey: contactFilterService.indexKey });
				onApply(response.data.data.id);
			}
		},
		[onApply, queryClient, contactAttributeOptions],
	);

	if (contactFilter == null || contactFilter.items.length === 0) {
		return null;
	}
	return (
		<FilterList>
			{contactFilter.items.map((filterItem, index) => (
				<ContactFilterBarItem
					key={index}
					item={filterItem}
					onRemove={() => handleRemoveFilterItem(contactFilter, filterItem)}
					onClick={onClick}
				/>
			))}
		</FilterList>
	);
};
